/**
 * "Help for this page": one plain-English entry per route in App.tsx.
 *
 * Written from what each page actually shows (its buttons, tabs, columns and
 * badges), for a reader who has never seen the app. No imports and only
 * erasable types, so node --test loads this file directly (test/page-help.test.js).
 * A new <Route path> needs an entry here or that test fails.
 */

export type PageHelp = {
  title: string;
  /** One sentence: what the page is for. */
  purpose: string;
  /** 3-6 short steps naming the real buttons, tabs and filters. */
  steps: string[];
  /** What the numbers, columns, colors and badges mean. */
  reading: string[];
  /** Setup the page depends on, limits, who can do what. */
  goodToKnow?: string[];
};

const PICK_CLIENT =
  "Everything here is for the client named at the top of the sidebar. Click that name to switch to another client.";

export const PAGE_HELP: Record<string, PageHelp> = {
  "/publishing": {
    title: "Publishing",
    purpose: "Write, review and schedule Facebook and Google Business Profile posts for this client.",
    steps: [
      PICK_CLIENT,
      "Choose Facebook or Google Business Profile, then a linked page or location.",
      "Write a post, add an optional image or link, then save the draft.",
      "A superadmin can publish now or approve a scheduled time. Scheduled times use US Eastern.",
      "Check the delivery result in the list. Cancel a scheduled post before it starts sending.",
    ],
    reading: [
      "Drafts do not publish. Only an approved post with a valid provider connection can send.",
      "Scheduled posts are checked every five minutes. Google or Facebook may take extra time to show an accepted post.",
      "Needs review means delivery was uncertain. Check the provider before creating another post.",
    ],
    goodToKnow: ["Use Setup, Connections to link a Facebook page or Google Business Profile location. Google needs Business Profile permission. No ad budgets or bidding settings change here."],
  },
  "/": {
    title: "Agency overview",
    purpose: "A quick health check of the whole agency: money spent on ads, which clients need attention, and how much work is open.",
    steps: [
      "Pick the dates at the top right: the last 7, 30 or 90 days, this week, last week, month to date, last month, or your own dates on the calendar. Every number and chart on the page follows them.",
      "Read the four cards across the top. Click a card to jump to the page behind it.",
      "Look at Needs attention under the client table: the clients with the weakest health first. Click one to open their profile.",
      "In Client performance, use Search clients, Show (for example At risk or Missing connections) and Sort to find a client.",
      "Use Agency overview for clients and priorities; switch to Analytics for charts. Press Refresh to reload the page's numbers.",
    ],
    reading: [
      "Managed spend: all ad money spent for every client in the window.",
      "Average health: the average client health score, out of 100. Higher is better.",
      "At risk: clients whose health score says they might leave. The small line under it counts clients we cannot score because data is missing.",
      "Clients with spend: how many active clients had ad spend in the window.",
      "Health score color follows retention risk: green is low, amber medium (under 70), red high (under 50). Grey means too few signals to trust the number.",
      "Coverage dots (Ads, GHL, calls): a filled dot means that data source is connected for the client. An empty dot means it is missing.",
      "Spend movement: how this window's spend compares with the window before it.",
    ],
    goodToKnow: [
      "The freshness note says how recent the imported ad data is. Ad platforms can still update the last few days.",
      "Spend fills in from the nightly Google Ads sync, or from entries on each client's Ad spend page.",
    ],
  },

  "/standup": {
    title: "Morning standup",
    purpose: "Your list for today: priorities, overdue work and follow-ups, written up each morning.",
    steps: [
      "Open the page at the start of your day and read Mine.",
      "Admins can switch to Agency to see the standup for the whole team.",
      "Press Refresh to rebuild it with the latest tasks.",
      "Do the work in the Work queue, then come back tomorrow for a fresh list.",
    ],
    reading: [
      "The date at the top is the day the standup is for. It rebuilds each time you open it or press Refresh.",
      "Click a task line to open it. Under Needs an owner, Assign gives it an owner.",
      "\"Nothing needs you this morning\" means no open priorities, overdue work or follow-ups were recorded for you today.",
    ],
    goodToKnow: ["The standup is built from tasks and follow-ups already in the app. If a task is missing here, check it has an owner and a due date in the Work queue."],
  },

  "/tasks": {
    title: "Work queue",
    purpose: "Every task for the agency and its clients, in one place, so nothing gets missed.",
    steps: [
      "Choose a view at the top: List (grouped by status), Table (a grid you can sort) or Board (columns you drag cards between).",
      "Use the search box, the owner filter (Anyone, Unassigned or a person) and the priority filter to narrow the list.",
      "Click the circle next to a task to mark it done. Click it again to reopen it.",
      "Click a task's date, priority, owner or status chip to change it right there.",
      "Drag a task onto another status group or column to move it. In the list you can also drag to reorder.",
      "Click a task to open its details, or press New task to add one.",
    ],
    reading: [
      "Statuses: To do, In progress, Escalated (needs a manager), Snoozed (hidden until a date) and Done.",
      "A red date means the task is overdue.",
      "Priority runs from Critical (most urgent) to High, Medium and Low.",
      "The client name under a task says who it is for. \"Agency\" means internal work.",
      "The number next to Work queue in the sidebar counts open critical and high-priority tasks, plus client requests.",
      "Where a task came from: Client request means the client sent it from their portal. Slack and Email mean NOLA made it from a watched Slack channel or the connected inbox.",
    ],
    goodToKnow: [
      "In the Table view, the buttons above the grid filter by one status at a time. On phones the Table view shows as the List.",
      "Moving a task to Snoozed opens its details so you can pick when it comes back.",
      "Superadmins can press Import to turn the last week of Fireflies meetings, or the last two days of the Gmail inbox, into tasks. Gmail works once it is allowed in Settings, Integrations; emails already read are never imported twice.",
      "Slack channels are picked per client in Setup → Slack. The inbox is allowed in Settings, Integrations, Google Workspace.",
    ],
  },

  "/approvals": {
    title: "Approvals",
    purpose: "Changes the AI analyst suggests wait here until a person checks them and says yes or no.",
    steps: [
      "Use the Status filter to pick which approvals to see. Pending is where new ones land.",
      "Click the action name to open the full proposal.",
      "Read the reasoning, the expected outcome and the risk if it is wrong.",
      "Add decision notes, then press Approve proposal or Reject.",
      "Press Refresh to check for new proposals.",
    ],
    reading: [
      "Risk: HIGH (red) or lower (amber). High-risk proposals need a superadmin to approve them.",
      "Status: Pending, Approved, Rejected, Executed (the change was made) or Rolled back (the change was undone).",
      "Proposed details: the exact values the change would use.",
    ],
    goodToKnow: ["Approving records your decision. It does not change anything in Google Ads or any other platform by itself; that is a separate step."],
  },

  "/ads": {
    title: "Ads",
    purpose: "Every client's Google Ads and Local Services results in one place: spend, clicks, conversions, calls and revenue, with charts.",
    steps: [
      "Pick the dates at the top: the last 7, 30 or 90 days, this week, last week, month to date or last month. For other dates, pick a start and an end day on the calendar and press Apply.",
      "Read the tiles. Each one compares with the same number of days just before.",
      "Switch the trend chart between spend, clicks and conversions, or between Google Ads and Local Services.",
      "Sort the client table by any column, or search for a client.",
      "Click a client to open their ads report.",
    ],
    reading: [
      "Green and red follow whether the change is good news: a lower cost per conversion is green, and spend changes are grey.",
      "No change shown means that client's data does not cover the whole earlier period, so a comparison would mislead.",
      "Budget is this month so far against the client's monthly ad budget. A dash means no budget is set on the client.",
      "Stale means no successful Google Ads sync in two days, or no data at all in a range that ends in the last two days. An older range with no data is just before the account's history.",
      "ROAS counts only clients with job or won-deal revenue, against their own spend.",
    ],
    goodToKnow: ["Local Services is reported on its own from Sep 21, 2026. Before that it sat inside Google Ads, so while a comparison reaches back before that date Local Services is counted inside Google Ads on both sides. Calls are recorded from Sep 21, 2026.", "Meta and Bing are not connected yet."],
  },
  "/ads-campaigns": {
    title: "Campaigns",
    purpose: "Run this client's Google Ads from NOLA: see every campaign live, turn it on or pause it, change its daily budget, block searches, and create new Search campaigns.",
    steps: [
      PICK_CLIENT,
      "Click a campaign's status to turn it on or pause it, or its daily budget to change it. The menu at the end of the row also blocks searches (negative keywords).",
      "Press New campaign to build a Search campaign: budget and bidding, places, keywords, the ad. Draft with AI writes headlines and descriptions you can edit.",
      "Press Check with Google to have Google check everything without creating anything, then Create paused campaign. Turn it on from this page when ready.",
      "Changes made in NOLA, at the bottom, lists what was applied, what is waiting for approval, and lets a superadmin undo a change.",
    ],
    reading: [
      "Spend, conversions and cost per conversion are the last 30 days, straight from Google Ads.",
      "Under the status, Google's own verdict shows when it matters: Limited, Learning, Not showing.",
      "Run by Google marks Local Services and Smart campaigns: Google runs those, so they are changed in Google Ads.",
    ],
    goodToKnow: [
      "A superadmin's change is applied at once. Anyone else's goes to Approvals and is applied when a superadmin approves it.",
      "New campaigns are always created paused, Search only (no Display or partner sites), for people in the chosen places.",
      "Budgets move at most 20% at a time (KB-029 §6), and never past the client's daily spend cap.",
      "Before applying, NOLA checks the campaign still has the status or budget it had when the change was proposed; if someone changed it in Google Ads, nothing is sent.",
    ],
  },
  "/compare": {
    title: "Compare to peers",
    purpose: "See how this client does next to the other clients in its industry over the last 30 days, and which peer is doing best on each figure.",
    steps: [
      "Read Where it stands: each figure shows this client, the peer median, its rank (1 is best) and the best peer.",
      "Green means ahead of peers and red means behind, in the direction that is better for that figure (lower is better for costs and missed call-backs).",
      "Open a peer's name to see the same page for that client, then copy what is working for them.",
    ],
    reading: [
      "A dash means there is not enough data yet, for example fewer than 5 sorted LSA calls or fewer than 3 Google Ads conversions.",
      "Peers are the other active clients with the same industry on their Profile.",
    ],
  },
  "/jarvis": {
    title: "Jarvis",
    purpose: "The agency's assistant: ask it anything about clients, ads, health, calls, the work queue, approvals or the CRM, and it reads NOLA for you.",
    steps: [
      "Pick a starter question or type your own, for example \"Which clients need attention this week?\" or \"Add a task for Karim to call Vietti on Friday\".",
      "Watch what it reads as it works. Open What Jarvis read under an answer to see the pages it used and which AI answered.",
      "It reads what your login can see, and adds work queue tasks when you ask. It does not change ads, budgets, clients or settings.",
      "An owner picks Claude or OpenAI in Settings, Jarvis. If that one does not answer, the other does.",
      "Attach screenshots, photos, PDFs, spreadsheets or text files with the paperclip, by pasting, or by dropping them on the chat. Pick which AI answers in the message box.",
      "Your chats are listed on the left by day. Search them, open one to carry on, or delete it. New chat starts fresh. The panel button hides the list so the chat takes the full width.",
    ],
    reading: [
      "What Jarvis read lists the pages it used for an answer and which AI wrote it.",
      "Task added: a task Jarvis put in the work queue for you. Open work queue to change or finish it.",
    ],
  },
  "/ads-ai": {
    title: "Ads AI",
    purpose: "An AI that runs this client's Google Ads with you: it reads the live account and NOLA's records, explains what it finds, and proposes exact changes.",
    steps: [
      PICK_CLIENT,
      "Pick a starter question or type your own, for example \"Where is money being wasted?\" or \"Plan a campaign for appliance repair in Chicago\".",
      "Watch what it reads as it works. Each change it proposes shows as a card under its answer.",
      "Open Review controls for scheduled account reviews, the latest report and held recommendations. Configure the owner and policy in client Setup, Connections, AI ad management.",
      "Press Apply on a card to make that change in Google Ads, or Skip. A team member's proposals go to a superadmin in Approvals.",
      "Past conversations keeps every chat for this client; New chat starts fresh.",
    ],
    reading: [
      "Looked at lists what the AI read for an answer: campaigns, search terms, the weekly review, the playbook.",
      "Proposed change: waiting for a decision. Applied in Google Ads: done, and undoable from Campaigns or Approvals. Skipped: nothing was sent.",
    ],
    goodToKnow: [
      "Scheduled management starts off. Monitor mode reads and reports; Propose mode can queue supported changes. Every ad change still needs explicit human approval, and new campaigns start paused.",
      "Incomplete reviews, OpenAI concerns, stopped policies and evidence older than 24 hours block scheduled proposals. Stop AI management does not pause existing Google campaigns.",
      "The AI never changes Google Ads on its own. Every change passes the same checks as the Campaigns page: budgets move at most 20% at a time, raises 3 days apart, within the client's daily cap; new campaigns start paused.",
      "It runs on OpenRouter when an OpenRouter key is set in Settings, Integrations, otherwise on the Anthropic or OpenAI key already there.",
      "It follows the agency playbook (the $50 search term rule, research and job negatives, 3+ conversions become exact keywords) and can look up the knowledge base.",
    ],
  },
  "/facebook-ads": {
    title: "Facebook ads",
    purpose: "This client's Facebook and Instagram ads: what the spend bought, and every campaign, ad set and ad with its switch and budget.",
    steps: [
      PICK_CLIENT,
      "Pick the dates at the top: a preset such as Last 30 days or Month to date, or your own dates on the calendar. The tiles, chart and campaign figures follow them. With more than one ad account, pick the account beside it.",
      "Open a campaign to see its ad sets and ads with their last 30 days, a thumbnail of each ad, and a link to the ad on Facebook.",
      "Click a status to turn a campaign, ad set or ad on or pause it, or a daily budget to change it. The menu at the end of the row does the same.",
      "Press Sync now to read Facebook again. It is read every 12 hours on its own.",
      "Changes made in NOLA, at the bottom, lists what was done, what waits for approval, and lets a superadmin run an approved change.",
    ],
    reading: [
      "Leads are what Meta counts as a lead: forms filled on Facebook and leads the website pixel reports. Messages and calls from ads show under the Leads tile when there are any.",
      "Cost per lead, click rate and cost per 1,000 views show a dash when there is nothing to divide by, never $0.",
      "Arrows compare with the same number of days just before. Green is good news: more leads, a lower cost per lead. Spend is grey: more spend is a plan, not a result.",
      "Campaign paused or Ad set paused under an ad means a level above it is off.",
    ],
    goodToKnow: [
      "Facebook is read through Windsor. A superadmin links the ad account in Setup, Connections.",
      "Changes are off for each account until a superadmin turns them on (here or in Setup, Connections). A superadmin's change applies at once; anyone else's goes to Approvals.",
      "Budgets move at most 20% at a time. Only a daily budget the campaign or ad set owns can be changed here; lifetime budgets stay in Ads Manager.",
      "After every change NOLA reads Facebook back and only says Done when Meta shows it.",
    ],
  },
  "/facebook-page": {
    title: "Facebook page",
    purpose: "This client's Facebook page: followers, how many people see its posts, engagement, every post with its figures, and publishing a new post.",
    steps: [
      PICK_CLIENT,
      "Pick the dates at the top: a preset such as Last 30 days or Month to date, or your own dates on the calendar. With more than one page, pick the page beside it.",
      "Switch the chart between Viewers, Engagements and New follows.",
      "Sort the posts by Newest or Most engaged. The arrow at the end of a row opens the post on Facebook.",
      "Press New post to write one. Write with AI drafts it from the client's profile and best posts; Improve with AI tidies what you wrote. Add a link or a public photo address if you want.",
    ],
    reading: [
      "Followers is the page's total now, with the change across the range under it.",
      "Viewers a day is the average number of people who saw the page's content each day.",
      "Engagements are reactions, comments and shares. A post's figures are for its whole life, not just the range.",
    ],
    goodToKnow: [
      "The page is read through Windsor every 12 hours, or now with Sync now. A superadmin links the page in Setup, Connections.",
      "Posting is off for each page until a superadmin turns changes on. A superadmin's post goes out at once; anyone else's goes to Approvals first.",
      "A photo post has no separate link, so a link you add goes at the end of its text.",
    ],
  },
  "/competitors": {
    title: "Competitors",
    purpose: "Who this client competes with on Facebook and Instagram: the ads they run, their own posts, and which posts did far better than usual.",
    steps: [
      PICK_CLIENT,
      "Find competitors searches Instagram and Meta's Ad Library for what the client does and where (about 2 credits). With an AI connected it marks direct competitors, nearby ones and big brands. Press Add on the ones worth watching.",
      "Or press Add competitor and paste their Facebook page link (or name) and their Instagram handle. Mark the client's own page too, so it shows as You.",
      "Compare: press Scan all, or Scan now on a row, to read their live ads. Click a row to see the ads; the row menu edits, pauses the weekly check or removes them.",
      "Their posts: their newest Instagram and Facebook posts; Read their posts pulls them now (about 1 credit an account). The ones that got at least twice their usual views or likes are on Viral posts, From competitors, which also searches the whole niche.",
      "Click a post to watch it, ask Why it worked, or Write our version into Content review.",
    ],
    reading: [
      "Active ads is Meta's own count; the small number under it is how many the check read (up to 60). New in 7 days, longest running and video / image are counted among the ads it read.",
      "Ads with the same words are shown once, with how many ads run them.",
      "Longest running is a hint, not proof: nobody keeps paying for an ad that loses money, but Meta shows no spend or results for these ads.",
      "Could not check means the scan did not read the Ad Library, so it says nothing about whether they advertise. Only No active ads means a clean scan found none.",
    ],
    goodToKnow: [
      "Scans read Meta's Ad Library through Scrape Creators (1 to 2 credits a competitor), or through Apify when Scrape Creators is not set up. Opening this page costs nothing; only Scan buttons and the weekly check do.",
      "An administrator adds the Scrape Creators key (or an Apify token) in Settings, Integrations.",
    ],
  },
  "/ads-review": {
    title: "Weekly Google Ads review",
    purpose: "Once a week NOLA reads this client's Google Ads and lists what breaks a playbook rule, with the rule behind each finding.",
    steps: [
      PICK_CLIENT,
      "Read Ready to send to approvals: each finding there is one exact change, like a negative keyword, or every app, game and kids placement to exclude at once.",
      "Press Send to approvals to put that change in Approvals, or Dismiss if you want to keep it. Undo brings a dismissed finding back.",
      "Under Needs a person, decide in Google Ads, or press Make a task to put it in your Work queue.",
      "Press Show more under a finding to see its full list of keywords, ads, changes or placements.",
      "Press Run review now to read the account again today.",
    ],
    reading: [
      "The group with the most urgent finding comes first. High marks the findings with the most money or traffic at stake.",
      "The line of numbers is the last 30 days: spend, clicks, conversions or impression share.",
      "The rule line names the playbook document and section, for example KB-029 §2. Click the code to open it in the Knowledge base.",
      "When a client has more than one Google Ads account, each finding names its account.",
    ],
    goodToKnow: [
      "Nothing on this page changes Google Ads by itself. Send to approvals puts the exact change in Approvals; approving it there applies it in Google Ads, and a superadmin can undo it.",
      "A search term that converted in the last 90 days is never suggested as a negative.",
      "Dismissed search terms and placements stay hidden for 90 days; weekly checks such as Quality Score come back each week until fixed. A dismissal can be undone for an hour.",
      "The review runs by itself once a week for every client with a linked Google Ads account.",
    ],
  },
  "/ads/:id": {
    title: "Client ads report",
    purpose: "One client's ads results: totals, the daily trend, campaigns, calls from ads and revenue, with team-only budget and alerts.",
    steps: [
      "Pick a range at the top. It carries over from the Ads page.",
      "Read the tiles, then switch the trend chart between spend, clicks and conversions.",
      "Sort the campaign table to see which campaigns cost the least per conversion. Click a campaign to open it in Campaigns, where it can be paused or its budget changed.",
      "Check What counted as a conversion, Calls from ads and Revenue for what the ads produced.",
      "Press Analyze with AI to open the Ads AI, ready to review this account and propose the changes that matter most.",
      "Read the Team only area for budget pace, open anomalies and changes made from NOLA before talking to the client.",
    ],
    reading: [
      "What counted as a conversion lists Google's own conversion actions: a phone click or a page visit is not a booked job, so compare cost per conversion with care.",
      "Calls from ads are Google forwarding-number calls. They are counted per campaign but never matched to a job. Past two weeks the chart shows weeks.",
      "Revenue comes from the job export when there is one, otherwise from GHL deals marked won. The two are never added.",
      "Team only: budget and anomalies are never shown to a client.",
    ],
    goodToKnow: ["Campaign detail starts on Sep 21, 2026. Earlier dates show account totals only."],
  },
  "/health": {
    title: "Client health",
    purpose: "A score out of 100 for every client, so you can spot accounts that might leave before they do.",
    steps: [
      "Type in Find a client to jump to one client's row.",
      "Read the Risk column, then the four scores beside it to see what pulls the total down.",
      "Click a client's name to open their profile.",
      "Scroll to Findings for problems the system found, grouped by client. Click Open report to open the client (an ad anomaly opens its Ads report).",
      "Admins can press Recalculate all to rescore every client, or use a client's ... menu to rescore one. Mark reviewed clears a finding (every repeat of it clears at once).",
    ],
    reading: [
      "Performance: open ad alerts and anomalies (one deduction per kind), and whether a linked Google Ads account has data this week.",
      "Communication: recent meeting notes and logged client contacts.",
      "Signal: how fresh the client's data is. Low means data feeds are old or missing.",
      "Execution: overdue tasks, critical or high tasks open more than a week, and approvals waiting more than a week.",
      "Score color follows retention risk: green is low, amber medium (under 70), red high (under 50). Grey means too few signals to trust the number.",
      "\"Thin data\" means Signal or Communication is under 70, so the score may not tell the full story. When that is true for every client, the page says so once at the top instead.",
      "Finding badges: Critical and High are red, Medium is amber. Days is how many days the same finding came back; Last seen and First seen say when.",
    ],
    goodToKnow: ["Scores reflect what is recorded in the app, not verified business results."],
  },

  "/websites": {
    title: "Website visitors",
    purpose: "Every client website that has the NOLA tracking script: how many people visit, who is on it now, and whether it is up.",
    steps: [
      "Scan the cards. Sites that are down always show first.",
      "Use the sort buttons: Traffic (most visitors), Change (biggest rise) or Name.",
      "Click a site's name to open its full Website analytics.",
      "Press Refresh data to update the numbers.",
    ],
    reading: [
      "Visitors, 7 days: people who visited in the last 7 days.",
      "Change: how that compares with the 7 days before. Up is good.",
      "Live: people on the site right now.",
      "\"Down\" in the count line means a site failed its last uptime check.",
    ],
    goodToKnow: ["A site shows here after it is created on the client's Tracking page and its snippet is pasted into the client's pages."],
  },

  "/conversions": {
    title: "Conversions",
    purpose: "Checks that every client's booked and completed jobs are being sent back to Google Ads, and shows what the last 30 days cost and earned.",
    steps: [
      "Check the four figures at the top: Google connection, Nightly syncs, Sending conversions to Google and Stuck. The Sending column says how each client sends them.",
      "Look down the table for red or amber badges.",
      "Click a client's name to set up their conversion sync or fix a problem.",
      "Press Refresh after making a change.",
    ],
    reading: [
      "A conversion is a result you want from an ad, like a booked job. Sending it back to Google Ads helps Google find more people like that customer.",
      "Conversions column: Direct (sent straight to Google Ads, best), Sheet (sent through a Google Sheet), Not connected (has Google Ads but nothing is being sent) or No Google Ads.",
      "Queue: how many conversions are waiting or failed. Red means some failed.",
      "ROAS (return on ad spend): revenue divided by ad spend. 4.0× means $4 back for every $1 spent.",
      "Offline conversions credited: the bookings Google Ads counted itself. It overlaps our own count, so it is never added to it.",
    ],
    goodToKnow: [
      "If Google connection says Not connected, go to Settings → Integrations → Google Workspace, press Reconnect Google and allow Data Manager.",
      "Nothing refreshes on its own until Nightly syncs is On.",
    ],
  },

  "/crm": {
    title: "Pipeline",
    purpose: "The agency's own sales: every deal from a lead form, a booking page or added by hand, by stage.",
    steps: [
      "Drag a card to another column to move the deal, or use the arrows in its corner (on a phone, that is the way to move it). Moving to Won asks for the contract amount; Lost asks why.",
      "Click a card to open the contact: their answers, calls, notes and the deal's stage, amount and owner.",
      "Press New deal, then search for an existing contact by name, email or company. Choose New contact to add someone; an email already in the CRM reuses that contact.",
      "Switch to Closed for won, lost and not-a-fit deals.",
    ],
    reading: [
      "The tiles: open deals and their total amount, money won since the 1st of this month (Eastern), calls booked in the next 7 days, and contacts added this week.",
      "Each column shows how many deals it holds and their total. A card shows the amount, the next booked call (Eastern) and the owner's initials.",
      "Qualified leads from a form start in Qualified; answers marked Not a fit go straight to Not a fit on the Closed tab. Booking a call moves New lead, Qualified and Follow-up deals to Call booked. A no-show moves Call booked to Follow-up.",
    ],
    goodToKnow: [
      "Won deals stay on the board for 90 days. From a won deal's contact, a superadmin can press Make NOLA client to add it to the client list as a prospect.",
      "New leads and booked calls are sent to Shawn in Slack once the Slack bot and his Slack user are set in Settings.",
    ],
  },
  "/crm/contacts": {
    title: "Contacts",
    purpose: "Everyone in the agency's CRM, with their current deal, newest activity first.",
    steps: [
      "Search by name, email, company or phone, or filter with the Stage and Source pills.",
      "Click a row to open the contact. Use Email, Call or Text to reach them from your device, and log what happened in the box under their deal.",
      "In the contact, Book a call puts a call on one of your calendars, and Follow up adds a dated task to the Work queue that links back to them.",
      "Press New contact to add someone by hand.",
    ],
    reading: [
      "Stage and Amount are the contact's open deal, or their newest one when nothing is open.",
      "Source says how they arrived: a form, a booking page, or added by hand. Came from (inside the contact) shows the ad tags that came with the visit.",
    ],
    goodToKnow: [
      "A contact is matched by email, then by phone, so a second form fill updates the first record and only fills in what was missing.",
      "Texting and calling inside NOLA (Twilio) are not set up yet; the Call and Text buttons use your phone or computer.",
    ],
  },
  "/crm/forms": {
    title: "Forms",
    purpose: "Lead forms that ask a few qualifying questions one at a time, add the lead to the pipeline and can book the call right away.",
    steps: [
      "Press New form and start from the casino survey or a blank form.",
      "Questions: write each question and its answers. Tick Not a fit on any answer that should disqualify the lead.",
      "After submit: pick the calendar qualified leads book on, and write what qualified and not-a-fit leads see.",
      "Press Preview to try it, then Go live. Share gives the link and the embed code for a website.",
      "Leads lists every submission; click one to open the contact.",
    ],
    reading: [
      "Leads counts every submission; Qualified counts those with no Not a fit answer.",
      "Draft forms only open for signed-in staff (Preview). Live forms open for anyone with the link.",
    ],
    goodToKnow: [
      "The form pages load on their own small bundle, so they open fast on phones and inside an embed.",
      "Preview of a draft never saves a lead. Testing a live form does, so use your own email and delete the contact after.",
    ],
  },
  "/crm/bookings": {
    title: "Bookings",
    purpose: "The calendars people can book with you, and every call booked on them.",
    steps: [
      "Press New calendar to set its hours, call length, gaps between calls, how soon and how far out people can book, and where the call happens.",
      "Open a calendar's booking page or copy its link from the table. Forms can also show the calendar right after a qualified answer.",
      "Under Calls, switch between Upcoming, Past and Cancelled. Mark past calls Showed or No-show, or cancel an upcoming one.",
    ],
    reading: [
      "Times in the Calls table are Eastern. Visitors see open times in their own time zone.",
      "A calendar's open times are its hours minus calls already booked with the same host and, once Calendar is allowed, busy time on the host's Google calendar.",
    ],
    goodToKnow: [
      "With Calendar allowed (Settings, Integrations), each booking adds an event with a Google Meet link and Google emails the invite. Without it, bookings still save but no invite goes out.",
      "Cancelling here removes the Google event, and Google emails the guest. A lead can cancel or pick a new time from the link on their confirmation.",
    ],
  },
  "/crm/drips": {
    title: "Drips",
    purpose: "Short series of emails for leads who are not ready to talk yet, sent a few days apart until they book a call.",
    steps: [
      "Press New drip. On Emails, write each email and how many days it waits after the one before. The preview shows it with sample details.",
      "Use the merge field buttons to add someone's first name or company. {{first_name|there}} says \"there\" when the name is empty.",
      "On Settings, choose when people start: a form (any answer, qualified or not a fit) or a deal reaching a stage. Add your mailing address.",
      "Press Send me a test to get the selected email yourself, then Go live.",
      "People lists everyone in the drip and their next email. Add people by hand there, or Stop one person.",
    ],
    reading: [
      "Emails go out between 9am and 5pm Eastern. One due at night waits for 9am.",
      "Active is how many people are still getting emails; Finished got them all; Sent 30 days counts emails sent in the last month.",
      "A drip stops for a person when they book a call (if ticked), when their deal is won, or when they unsubscribe.",
    ],
    goodToKnow: [
      "Drip emails come only from the No Limits sending domain. Until it is verified (Settings, Integrations, Email), they wait and the page says so.",
      "A form or stage never puts someone through the same drip twice. Adding them by hand starts it over.",
    ],
  },
  "/pages": {
    title: "Pages",
    purpose: "Landing pages for the agency's offers: build one from a template, publish it, and the leads from its form land in the CRM.",
    steps: [
      "Press New page. Pick a template (Preview shows every step at desktop and phone size) or Blank page, name it and press Create.",
      "The editor opens. Click any words on the page to change them, and use the panels for sections, colors, the form and the link.",
      "Press Publish in the editor. The page goes live at its link, and a draft form linked to it goes live with it.",
      "Back here, search by name or link, or sort by last change, name or status.",
      "On a card: Preview opens the latest version (drafts too), Share copies the link or the embed code, Duplicate makes a draft copy, and Delete removes the page.",
    ],
    reading: [
      "Live: anyone with the link can open the page. Draft: only people with its private Preview link can see it.",
      "The grey line covers the last 30 days: views, leads (form answers sent from this page) and how many visitors became leads.",
      "No visits yet: the page is live but nobody opened it in the last 30 days. Not published: the page is a draft.",
    ],
    goodToKnow: [
      "Leads from a page's form land in the CRM like any other form's, and count toward that page's leads.",
      "Put pages on your own web address in the Domains tab.",
      "Administrators and team members can build and publish pages. Read-only accounts can look and preview.",
    ],
  },
  "/pages/domains": {
    title: "Domains",
    purpose: "Put your landing pages on your own web address, such as go.yourdomain.com.",
    steps: [
      "Type the domain in Add a domain (a subdomain like go.yourdomain.com is easiest) and press Add.",
      "Copy the DNS records shown under it into your DNS provider, such as GoDaddy or Cloudflare.",
      "Press Check. DNS changes can take a few minutes. Opening this tab checks every waiting domain again.",
      "Once it shows Live, pick a Homepage: the page people see at the bare address.",
      "Remove takes a domain off. Its DNS records stay at your provider until you delete them there.",
    ],
    reading: [
      "Live: the domain is connected and shows your published pages. Waiting for DNS: the records are not in place yet. Error: the host refused the domain, and the reason shows under it.",
      "Every published page works at https://<domain>/<page link>, and the homepage at https://<domain>/.",
      "Last checked is the last time the connection was tested, in Eastern time.",
    ],
    goodToKnow: [
      "Only administrators can add, check and remove domains or pick a homepage.",
      "A draft page picked as the homepage stays hidden until you publish it.",
      "If the tab says custom domains are not set up on this server yet, the server is missing its Vercel connection.",
    ],
  },
  "/pages/:id": {
    title: "Page editor",
    purpose: "Build and edit one landing page: its sections, words, look, form and link.",
    steps: [
      "Click a section on the page to select it, then click its words to type over them. The Section tab on the right has the rest of its settings.",
      "Use Add section in the left panel to add one, and drag sections there to change their order. Pages with several steps list them at the top.",
      "On the Page tab, pick the look, colors and fonts, the CRM form and the page link. The AI tab rewrites the words for you.",
      "Switch Desktop, Tablet and Phone at the top to check each size, and Try it to click through the page like a visitor.",
      "Drafts save on their own. A live page keeps your edits until you press Publish changes.",
    ],
    reading: [
      "The note beside Publish says whether your changes are saved, saving, or waiting to be published.",
      "Version history lists earlier versions. Restoring one is a normal change, so Undo brings the page back.",
    ],
    goodToKnow: [
      "Preview opens the page in a new tab exactly as visitors will see it, drafts included.",
      "Share has the public link on every domain and the embed code. More has Save as template and Unpublish.",
    ],
  },
  "/traction": {
    title: "Traction",
    purpose: "The agency's EOS planning tools: quarterly goals, weekly numbers, open issues, the vision and the weekly leadership meeting.",
    steps: [
      "Rocks: pick a quarter at the top left, press New rock to add one, or click a rock to update its status and progress.",
      "Scorecard: click a week's cell to type that week's number. Fill this week (admins) fills every number the app already has.",
      "Issues: press Add issue, or Discuss / solve to work one through.",
      "Vision: read the agency's V/TO; admins press Edit vision to change it.",
      "Meetings: press Schedule meeting, open it, tick off each part of the agenda as you go and press Complete meeting at the end.",
    ],
    reading: [
      "EOS (Entrepreneurial Operating System) is the planning method these tabs follow. The V/TO (vision/traction organizer) is its one-page plan.",
      "Rocks: the 3 to 7 most important goals for a quarter, each with one owner. Off track adds the rock to Issues.",
      "Scorecard: the last six weeks, each filed under its Monday. Green meets the goal, red misses it; ≥ means the goal is a minimum, ≤ a maximum.",
      "Issues: problems to identify, discuss and solve (IDS). Lower priority numbers come first.",
      "Level 10 meeting: the weekly leadership meeting, run in the same order every time, with that week's scorecard, the quarter's rocks and the open issues under their part of the agenda.",
    ],
    goodToKnow: [
      "Each Monday night the app fills last week's numbers it can work out (clients, health, tasks, approvals, Google reviews, call scores); Fill this week fills the week so far. MRR, leads, proposals, content and Neighborly pages are typed by hand. A number typed by hand is never overwritten.",
      "The People tab (reviews of each team member) is only shown to superadmins. Only superadmins can edit Vision.",
    ],
  },

  "/agency/projects": {
    title: "Projects",
    purpose: "Every project across the agency, client and internal, as a board by status, a list or a timeline of deadlines.",
    steps: [
      "Choose a view: Board, List or Timeline.",
      "Use the search box, the Client filter (Agency means internal projects) and the Status filter to narrow things down.",
      "Click a project to open it: update a phase, or change its status, priority and due date.",
      "On the board, use the arrows on a card to move a project to another status.",
      "Press New project to start one. Most types come with a standard set of phases.",
    ],
    reading: [
      "Board columns: Planning, Active, Blocked, On hold and Completed. Cancelled shows only when a cancelled project is listed.",
      "Phases: phases done out of the total, for example 2 of 5. A skipped phase counts as done.",
      "A due date in red has passed on a project that is still open.",
      "\"Agency\" in place of a client means an internal project.",
    ],
  },

  "/team": {
    title: "Team",
    purpose: "How busy each person is, what they are good at, and who covers which client.",
    steps: [
      "Workload tab: see open, due today and overdue work for each person.",
      "Competencies tab: see who is Primary, Secondary or Learning for each skill.",
      "Client coverage tab: see which clients each person covers. Admins click a person to tick their clients.",
      "Press Refresh to update the numbers.",
    ],
    reading: [
      "Capacity: estimated hours on open tasks, compared with a 40 hour week. Red means overloaded, amber means close to full.",
      "Needs owner: tasks that nobody is assigned to.",
      "Overdue shows in red when it is above zero.",
      "Primary does the work first, Secondary covers, Learning is being trained.",
    ],
    goodToKnow: [
      "Tasks without an hour estimate count in the task columns but not in Capacity.",
      "Client coverage decides which clients appear on each person's morning standup. It does not limit what anyone can open.",
    ],
  },

  "/training": {
    title: "Training",
    purpose: "NOLA Academy: courses that teach how No Limits works, with lessons, videos and short quizzes.",
    steps: [
      "Pick a course from the list. Use Find a course or the filter (All, In progress, Completed) to find one.",
      "Switch between list and gallery with the view buttons.",
      "Open a lesson, read or watch it, then press Mark complete.",
      "If a lesson has a quiz, answer the questions and press Check answers.",
      "Use Continue where you left off to pick up where you stopped.",
    ],
    reading: [
      "Each course shows Not started, In progress or Completed.",
      "Continue opens the lesson you opened last and have not finished, on the course page and in the library alike.",
      "Quizzes need 80% to pass. Passing marks the lesson complete.",
    ],
    goodToKnow: [
      "Admins can press New course, Edit course or Edit lesson, and see everyone's results under Team progress.",
      "Leaving the lesson editor with unsaved changes asks first.",
    ],
  },

  "/prompts": {
    title: "Prompt library",
    purpose: "Ready-made AI instructions (prompts) the team uses, sorted by what they are for.",
    steps: [
      "Search prompts and their text, or pick a category from the Category filter.",
      "Click a prompt to open it. The copy button at the end of a row copies it without opening.",
      "Press Copy prompt to paste it into another tool.",
      "To run it on a client's data, press Use in AI analyst, or pick the prompt in the AI analyst.",
      "The History tab shows earlier versions of the prompt.",
    ],
    reading: [
      "Model tier: which AI model the prompt is written for. Opus is the stronger, slower model; Sonnet is faster.",
      "Earlier versions: how many times the prompt has been edited. Each old version is kept in History.",
      "% helpful: the share of thumbs up the AI analyst's answers got with this prompt in the last 30 days. It shows once there are 3 votes.",
    ],
    goodToKnow: ["Only administrators can edit prompts. Every edit keeps the old version in History."],
  },

  "/knowledge": {
    title: "Knowledge base",
    purpose: "The agency's standards and playbooks, which the team and the AI analyst both follow.",
    steps: [
      "Search documents in the list, or pick one under Recently updated.",
      "Click a document to read it.",
      "Hide or show the document list with the button beside the search.",
    ],
    reading: [
      "Documents are listed by code (KB-001, KB-002 and so on). A tag after a title marks a document written for one kind of client (Franchise, Junk King) or one with no text yet.",
      "Recently updated shows the six documents changed most recently.",
    ],
  },

  "/knowledge/:code": {
    title: "Knowledge base document",
    purpose: "One agency standard or playbook, to read or (for admins) edit.",
    steps: [
      "Read the document. The code, version and last update are under the title.",
      "Press Link to copy a link to it, or Copy to copy the whole text.",
      "Admins: press Edit, make changes, then press Save. Cancel throws the changes away.",
      "Admins: press History to see earlier versions and restore one.",
      "Use the list on the left to go to another document. On a phone, press All documents at the top.",
    ],
    reading: [
      "While editing, the top bar says Unsaved changes, Saving… or No changes.",
      "Restoring a version only loads it into the editor. Nothing changes until you press Save.",
    ],
    goodToKnow: ["If someone else saved the document while you were editing, copy your changes, reload, and apply them again."],
  },

  "/settings": {
    title: "Settings",
    purpose: "Your account, plus (for admins) the connections to the outside services that fill the app with data.",
    steps: [
      "Use the menu on the left: Overview, Appearance, Integrations, Members, System & security or Activity.",
      "Overview: Integration readiness lists the services that need attention first. Click one to open its card.",
      "Integrations: search or pick a Category, then open a service's card to add its keys and press Test connection.",
      "Members: click a person to edit their name and access. Invite sends a first password link to someone who has not signed in yet; Password link sends a new one to someone who has. Show narrows the list to the team, clients or inactive logins.",
      "Appearance: choose your theme, sidebar, accent, density and text size. The preview shows your choices.",
      "Activity: see when a key was saved, tested, replaced or removed, and by whom.",
    ],
    reading: [
      "Configured (green): the service's keys are saved. Setup needed: they are not. Last test failed (red): its last connection test failed and nothing has changed since; open the card to see why.",
      "The number next to Integrations counts configured services out of the total.",
      "Members: Active has signed in, Not signed in yet has a login but has never used it, Inactive can no longer sign in.",
      "System & security: Database, Credential vault and Background jobs turn red only when something is wrong. Worker activity shows when each background job last finished.",
      "Times are US Eastern (ET).",
    ],
    goodToKnow: [
      "Many pages stay empty until the matching service is connected here: Google Ads for spend, GoHighLevel (GHL) for pipeline, CallTrackingMetrics (CTM) for calls.",
      "Links for one client (its Google Ads and CTM accounts, GHL token, BrightLocal locations) live on that client's Connections page under Setup, and its tracking sites on its Tracking page, not here.",
      "Team members who are not administrators only see their own account here.",
    ],
  },

  "/client-settings": {
    title: "Client setup",
    purpose: "Everything that connects one client to its own accounts, plus any keys that belong to that client only. The Setup tabs at the top switch between Connections, Portal, Slack and API keys.",
    steps: [
      PICK_CLIENT,
      "Connections: connect the client's GoHighLevel (GHL) sub-account, link its Google Ads and CTM accounts (Link account, optionally for one territory; Unlink removes a wrong one), and link its BrightLocal locations.",
      "AI ad management: assign an active superadmin, choose Monitor or Propose, and set daily or weekly reviews. Propose also requires a goal, landing page, explicit campaign budget ceiling and confirmed tracking. All changes require human approval.",
      "Portal: choose which portal tools the client sees and manage its portal logins.",
      "Slack: pick a channel the bot is in and press Watch channel. Requests posted there become this client's tasks.",
      "API keys: press Add client key on a service when this client pays for its own account (for example its own OpenAI or CTM keys). Test checks a saved key, Replace changes it, Remove goes back to the agency key.",
    ],
    reading: [
      "Using this client's key: work for this client uses the key saved here.",
      "Using agency key: no client key is saved, so the agency key from Settings is used.",
      "Not set anywhere: neither this client nor the agency has a key, so that service does not run for this client.",
      "Stored key can't be read: the saved key is damaged. Press Replace and paste it again.",
      "Slack shows Not connected until the bot token is in Settings, and No channels yet until a channel feeds this client.",
    ],
    goodToKnow: [
      "Only superadmins can open these tabs.",
      "Unlinking an account stops new spend or calls importing for this client; what was already imported stays.",
      "Saved keys are never shown again, not even to you. To change one, replace it.",
    ],
  },

  "/clients": {
    title: "Clients",
    purpose: "Every current client (active, paused and prospects) with their tier, locations, budget and main contact.",
    steps: [
      "Type in Search clients to find a client by name, industry or contact.",
      "Use the status filter to show only active, paused or prospect clients.",
      "Filter by service tier (Reporting only, AI only, Standard, Fractional CMO).",
      "Sort by Name, Highest media spend or Most locations.",
      "Click a client to open their profile. Press Clear to reset the filters.",
    ],
    reading: [
      "Tier: the service package the client is on. Reporting only clients get tracking and reports, so their sidebar has no content, creative or project pages.",
      "Locations: how many markets (territories) the client has.",
      "Monthly media: the client's monthly ad budget.",
      "A Duplicate record badge means two clients share the same name. One of them may need cleaning up.",
    ],
  },

  "/clients/:id": {
    title: "Client profile",
    purpose: "Everything the team needs to know about one client: contacts, locations, brand voice and rules.",
    steps: [
      "Press Log contact at the top after any call, email or meeting with the client.",
      "Press Edit on Account overview to change tier, budget or contact details. Press Confirm numbers once the client confirms their booking rate and average job value.",
      "Press Add location to add a market with its own Google Business Profile and tracking number. Use Edit or Remove on a location's row to change it.",
      "Press Edit brand to set the brand voice, audience and selling points.",
      "Press Add guardrail for words, services or areas every draft must avoid or include. Admins can Remove one that is wrong.",
      "Press Work queue to see this client's tasks.",
    ],
    reading: [
      "The badge next to the name is the client's status. Active is green.",
      "Booking rate and Average job value say Confirmed once the client confirmed them. ROAS and the AI analyst use only confirmed numbers.",
      "The activity log is a diary of what happened on the account, grouped by day, each line with its time (US Eastern). Click a task in it to open the task.",
      "Syncs that went fine are under the Syncs filter, one line per nightly run; a failed one also shows under All, in red.",
    ],
    goodToKnow: [
      "The brand profile and guardrails are what AI drafts and reports use to sound like this client and stay out of trouble.",
      "Removing a location takes it off the profile. Calls, jobs and spend already recorded for it stay.",
    ],
  },

  "/dashboard": {
    title: "Client dashboard",
    purpose: "One client's results at a glance: what the ads cost, how many leads came in, and what they turned into.",
    steps: [
      PICK_CLIENT,
      "Choose the dates at the top: the last 7, 30 or 90 days, this week, last week, month to date, last month, or your own dates. The last 7, 30 and 90 days end yesterday, the last full day the nightly sync has, the same as the Ads pages.",
      "Read the cards at the top, then By platform, then Where leads came from.",
      "Press Manage spend (or Enter spend) to add or fix ad spend.",
      "Press Preview client portal to see exactly what the client sees.",
    ],
    reading: [
      "Leads: tracked calls (Google's own call records and CTM) plus forms, split out under the number.",
      "Booked jobs: jobs from the client's job export. \"Matched\" means tied back to a call or lead.",
      "Completed revenue: money from finished jobs. National Accounts (corporate-referred work) are left out.",
      "ROAS (return on ad spend): completed revenue divided by ad spend. 4× means $4 back for every $1.",
      "From a Google Ads click: the share of leads that came from a Google ad. Platform conversions in the table are each ad platform's own count, not leads.",
      "A dash (—) means there is no data for that number, for example no job export covers these dates. It is never a guess and never a 0.",
    ],
    goodToKnow: [
      "A notice at the top says when a call or spend feed stopped updating, and from when.",
      "Google's own call records start on Sep 21, 2026, so longer periods show calls from that day.",
      "Spend and leads always cover the same dates. The client portal still shows this week, month or quarter so far.",
    ],
  },

  "/call-intelligence": {
    title: "Calls & coaching",
    purpose: "The client's phone calls from ads, how the calls were handled, and their GoHighLevel pipeline.",
    steps: [
      PICK_CLIENT,
      "Ad calls tab: see the calls CTM recorded, with the time each came in. Search by caller, outcome or summary.",
      "Click a call for its details; press Read transcript to see what was said.",
      "Scoring and coaching tab: see how each Local Services Ads call was handled and a coaching note. Press Scan LSA calls to score the last 30 days.",
      "GHL activity tab: see pipeline stages and follow-up from GoHighLevel.",
      "Press Refresh to update.",
    ],
    reading: [
      "CTM calls: calls dialed straight from an ad to a CTM number, last 30 days. A dash means CTM is not updating or not linked.",
      "Google Ads calls: calls through Google's forwarding numbers, from Google call reporting.",
      "Answered: the share of CTM calls someone picked up. Booked: CTM calls marked booked.",
      "Call score: how well the call followed the NEPQ sales method, out of 10. Recovery chance: how likely a call that did not book can still be won back.",
    ],
    goodToKnow: [
      "Times are US Eastern. The call list shows the latest 200 calls at most.",
      "Each LSA scan transcribes and scores new calls with AI, which costs a little per call. Calls already scored are skipped.",
      "GHL activity is empty until the client's GoHighLevel location is connected in Setup → Connections.",
    ],
  },

  "/call-center": {
    title: "Call center reports",
    purpose: "Upload the client's call center export and get a written analysis of how calls turned into bookings.",
    steps: [
      PICK_CLIENT,
      "Click New report. Choose the call center export (required) and, if you have it, the appointment export.",
      "Set Period start and Period end, and any report options, then upload.",
      "Click History to see past reports. Click a row to read the report.",
      "In a report, use Ask this report to ask a question about it in plain words.",
    ],
    reading: [
      "Answer %: the share of calls that were answered.",
      "Booked from calls and Booking %: appointments booked on the calls in the call center export.",
      "Completed jobs: finished jobs from the appointment export, from every channel, so it can be higher than Booked from calls.",
      "ROAS (return on ad spend): revenue divided by ad spend, when spend is linked for the period.",
    ],
    goodToKnow: [
      "Adding the appointment export unlocks revenue, completion rates and ROAS.",
      "Files must add up to under 4 MB. The analysis is written by AI and can take a moment.",
      "Turn on Exclude National Accounts to report marketing-driven work only.",
    ],
  },

  "/ad-spend": {
    title: "Ad spend",
    purpose: "Record what the client spent on platforms that don't sync on their own, like Meta, Yelp or Angi, so costs and ROAS are correct.",
    steps: [
      PICK_CLIENT,
      "Choose the Entry date. For a week's total, use that week's Monday.",
      "Type the spend for each platform. Clicks and conversions are optional.",
      "Press Save entries. Only the platforms you changed are saved.",
      "Check Spend history below. Press Delete on an entry typed by mistake; Undo puts it back.",
    ],
    reading: [
      "Leave a box blank for no entry. Type 0 only when you know the spend was zero.",
      "CPA (cost per acquisition): spend divided by conversions. Lower is better.",
      "Total spend, Clicks and Conversions add up every entry, synced or typed, between the dates under each number.",
    ],
    goodToKnow: [
      "For a client with a linked Google Ads account, Google Ads, LSA and YouTube spend come in by themselves every night, so the form has no boxes for them.",
      "Synced spend can't be deleted here; only entries typed on this page can.",
    ],
  },

  "/tracking": {
    title: "Tracking",
    purpose: "Where the client's leads and booked jobs come from, and whether they are being sent back to Google Ads.",
    steps: [
      "With no client picked, this shows every client's tracking. Click a client to set theirs up.",
      "Results come first. Pick the dates at the top (a preset, month to date, last month or your own dates); the four numbers follow them.",
      "On the Setup tab, answer \"Where do this client's ads land?\" so the checklist shows the right steps. Press Change to switch it later.",
      "Work down the Tracking checklist. Tick manual steps when they are done.",
      "Upload the job export under Conversion sync; matches rebuild on their own after every upload and each night.",
      "Under Tracking numbers, press Import from CTM and Confirm each channel, or pick a channel beside an unmapped CTM number and press Add.",
    ],
    reading: [
      "Matched jobs: booked jobs we tied back to a call or lead, out of all booked jobs.",
      "Credited by Google: bookings Google Ads counted itself. A dash means Google has not reported the \"NL-AOS Booked\" action yet.",
      "Google spend: money spent on Google Search, Local Services Ads (LSA) and YouTube.",
      "ROAS (return on ad spend): job revenue divided by Google spend on the days the job export covers. When the export stops early, the page says so.",
      "GCLID or click ID: a code Google adds when someone clicks an ad. It lets us tie a lead back to that click.",
      "Unmatched jobs: jobs we could not tie to a call or lead, grouped by what the customer said when asked how they heard.",
      "Checklist: a grey circle is not started yet; an amber one had data and stopped.",
    ],
    goodToKnow: [
      "CTM is CallTrackingMetrics, the service that gives each ad its own phone number.",
      "Campaign spend and Google's own call records start on Sep 21, 2026, so longer periods show them from that day.",
      "Open \"How these numbers are counted\" under Where booked jobs came from for the exact rules.",
    ],
  },

  "/seo-writer": {
    title: "SEO writer",
    purpose: "Blog posts, service pages, location pages and website copy, each researched against the pages that rank for its keyword and written to beat them.",
    steps: [
      PICK_CLIENT,
      "Under About the client, press Read the website once. Every article is written from what it finds, and empty brand profile fields fill in.",
      "Add keywords, one per line, or click a suggestion from Search Console page 2 and 3. Pick the type, then Queue (they write in the background, a few every 5 minutes) or Write now for one keyword.",
      "Open an article. Its score is measured the same way as the top results; beat the score to beat. Close the gaps rewrites it to use the terms and subtopics it is missing.",
      "Before publishing lists the checks. Propose fixes asks the AI to fix what fails; approve or discard them. Then Send to review.",
      "Review queue shows every client's articles waiting for approval. After it goes live, Mark published with its address. Prospect audit checks a prospect's website and gives you a one-page report link to send them.",
    ],
    reading: [
      "Top results come from a web search for the keyword and area, not a rank tracker, so they can differ a little from one Google search.",
      "The AI never adds prices, years, awards or guarantees on its own. Check every fact before approving.",
      "Most franchise locations cannot edit their corporate site: use these for sites we control, Google Business posts or the corporate team.",
    ],
  },

  "/seo-plan": {
    title: "SEO plan",
    purpose: "This week's SEO work for one client, written from its recent calls, unanswered reviews, AI search results and citations.",
    steps: [
      PICK_CLIENT,
      "Press Write this week's plan. It takes about a minute. After the first one, a new plan comes every week.",
      "Work through This week, the tasks in order of what matters most.",
      "Posts: press Create drafts to save a post as a draft for every connected Facebook page, Google Business location and Zernio account. Approve each one on the Publishing page. Instagram needs a photo, so add one to its draft there.",
      "Use What callers ask in posts, on the Google Business Profile and on calls. Copy each review reply and post it on the review.",
    ],
    reading: [
      "Every fact comes from the client's own calls, reviews and data. Check offers and prices before approving a post.",
      "Website schema shows only for a client whose own site we can change.",
      "This week's tasks come from the Playbook tab first: due tasks from the No Limits SEO library. Mark one done and it comes due again on its schedule.",
    ],
  },
  "/ai-search": {
    title: "AI search",
    purpose: "See whether ChatGPT, Gemini and Claude name the client when a buyer asks them for help nearby, and which businesses they name instead.",
    steps: [
      PICK_CLIENT,
      "Check the questions. Each client starts with two per area, written the way a buyer asks. Add your own under the table, or press Turn off on one you do not want.",
      "Press Run now. Every question goes to all three with web search on, which takes about a minute. After the first run they run again every week.",
      "Press Named or Not named to read the full answer, the businesses it named and the sources it used.",
    ],
    reading: [
      "Named: the answer mentioned the client by name, or cited the client's own page.",
      "Named instead: the businesses the answers recommended in the last run, most often first. These are the clients' real rivals in AI answers.",
      "Each search costs about 3 to 4 cents in AI credit.",
    ],
  },
  "/keywords": {
    title: "Keywords",
    purpose: "Find keywords for a client with Google's search volume and bid estimates, keep the ones that matter in a tagged list, and see the searches their site already shows up for.",
    steps: [
      PICK_CLIENT,
      "Check the place at the top right. Numbers are for that place: the client's own city when one is saved, otherwise the United States. Use \"Other place…\" to pick any city, metro or state.",
      "Research: under Find ideas, type up to 20 seed keywords and/or a page URL, then press Get ideas. Under Check a list, paste up to 1,000 keywords and press Get numbers.",
      "Tick the keywords you want, choose a tag (Service, Brand, Location, Negative or Other) and press Save to list.",
      "Saved list: change a tag, add a note, remove a keyword, press Refresh numbers, or Export CSV.",
      "Search Console: pick 28 or 90 days, tick searches worth keeping and save them to the list the same way.",
    ],
    reading: [
      "Searches / mo: Google's average monthly searches over the last 12 months, for the chosen place.",
      "12 months: the same searches month by month, oldest on the left.",
      "Competition: how many advertisers bid on the keyword (low, medium, high), with Google's 0-100 score.",
      "Top of page bid: what advertisers paid to show at the top of the page. The low end is the 20th percentile, the high end the 80th.",
      "Search Console: clicks and average position for that exact search on the client's site in the last 90 days.",
      "BrightLocal: the best Google and Maps rank, and BrightLocal's monthly volume, when the Rank Tracker follows that keyword.",
      "Numbers as of: when Google's numbers were fetched. \"Refresh failed\" or \"Not fetched yet\" means Google refused for now; it is retried tonight.",
    ],
    goodToKnow: [
      "Numbers come from Google Keyword Planner through the agency's Google Ads connection. Nothing is changed in any Ads account. Until an admin connects Google Ads in Settings, Get ideas and Get numbers stay off.",
      "Saved numbers refresh by themselves once a month; Search Console is read again every week.",
      "Search Console needs an admin to reconnect Google once in Settings, and the Google account must have access to the client's property.",
      "Admins can change which Search Console property belongs to the client on the Search Console tab.",
    ],
  },

  "/website": {
    title: "Website analytics",
    purpose: "How people find and use the client's website: visits, where they came from, and which visits became leads.",
    steps: [
      PICK_CLIENT,
      "Pick the dates at the top: today, the last 7, 30 or 90 days, this week, last week, month to date, last month, or your own dates on the calendar.",
      "If the client has more than one site, pick it from the Website menu.",
      "Click any row in Traffic details (pages, sources, campaigns, devices, countries) to filter the whole page by it.",
      "In Conversions and attribution, switch the model and lookback window to see who gets the credit.",
      "Admins can press Settings to set money pages and uptime checks.",
    ],
    reading: [
      "Visitors: different people. Pageviews: pages they opened. Bounce rate: visits that left after one page (lower is better).",
      "The live badge counts people on the site in the last 5 minutes.",
      "Money pages: pages that show someone wants to buy, like a quote or booking page.",
      "First click gives credit to the first thing that brought someone in; last click gives it to the last thing before they got in touch.",
      "ROAS (return on ad spend): revenue divided by ad spend. With filters on, it is a minimum, because spend is never filtered.",
      "Uptime: the share of hourly checks where the site loaded.",
    ],
    goodToKnow: ["The page stays empty until the tracking script is in the funnel header. Set it up under Tracking script on the client's Tracking page."],
  },

  "/reputation": {
    title: "Reviews & citations",
    purpose: "The client's online reviews, the replies to them, and where their business listings are tracked.",
    steps: [
      PICK_CLIENT,
      "Check the cards: reviews in NOLA, average rating, new reviews in the last 30 days, and reviews rated 3 or less.",
      "Under Latest reviews, set Rating to 3 stars or less to see the ones that may need a reply. Press Show more for older reviews.",
      "Click a review to open it. Press Draft with AI, edit the reply, and save it. Imported Google reviews with a verified profile link can publish directly after you confirm.",
      "Press Sync now on the BrightLocal panel to pull new reviews. A client with no reviews shows Link BrightLocal to admins.",
    ],
    reading: [
      "Reply needed: a review rated 3 or less with no reply yet. A new one also raises an alert.",
      "Reviews in NOLA can be fewer than BrightLocal's count: reviews without a star rating or date, like Facebook recommendations, are not imported.",
      "A citation is a listing of the business on another site, like Yelp or a local directory. BrightLocal's Citation Tracker report checks them.",
    ],
    goodToKnow: [
      "Direct Google replies need Business Profile permission and a verified location link in Setup, Connections. Reviews from other sources can still be replied to externally and marked as posted.",
      "Draft with AI uses the AI service set up in Settings.",
    ],
  },

  "/analyst": {
    title: "AI analyst",
    purpose: "Chat with an AI that can read one client's data and answer questions about it.",
    steps: [
      "Pick a client in the Client menu (each chat belongs to one client).",
      "Type a question, or click one of the starter questions. Press Enter to send, Shift+Enter for a new line.",
      "Use the prompt picker in the message box to run a saved prompt from the Prompt library.",
      "Press New chat to start fresh. Old chats are in the list on the left, with a search box.",
      "Press Create review task to turn the latest answer into a task in the Work queue.",
    ],
    reading: [
      "If the analyst proposes a change, a Proposed action card with a Review in Approvals link appears under its answer, and stays there when the chat is reopened. Nothing changes until someone approves it.",
      "Each chat in the list is titled by its first question.",
      "\"Thinking\" means the answer is still being written.",
    ],
    goodToKnow: [
      "The AI can be wrong. Check the sources and how fresh the data is before acting on an answer.",
      "The analyst gives ROAS projections only once the client's booking rate and average job value are confirmed (Confirm numbers on the client's profile).",
    ],
  },

  "/content-ideas": {
    title: "Content pipeline",
    purpose: "The client's content on one board, from idea to posted: Ideas, Approved, In production, Ready to post and Posted.",
    steps: [
      PICK_CLIENT,
      "Press Generate with AI for a batch of ideas (each starts with a hook), Add idea to write one, or save one from Viral posts with Save as idea.",
      "Drag a card to another column to move it. On a phone, or if you'd rather pick, use the card's ... menu.",
      "Write it with AI (in the card's ... menu) has the AI write the draft in Content review, in the client's voice: a video idea becomes a video script, a Google post a GBP post, anything else a social post. The card moves to In production and shows a Draft link.",
      "Click a card to edit it or archive it. Switch to List for a table, with search and filters by Type, Platform or Priority.",
    ],
    reading: [
      "The line under a card's title is its hook: the first line or first 3 seconds.",
      "High marks a high-priority card. The clock shows how long ago it was added.",
    ],
    goodToKnow: [
      "Generate with AI reads the brand profile, the client's best recent Facebook posts and its viral posts from Viral posts and Competitors. To check the prompt first, use \"Write the prompt in the AI analyst instead\".",
      "Blog posts and emails have no draft type in Content review yet, so Write it with AI is not offered for them.",
    ],
  },

  "/viral": {
    title: "Viral posts",
    purpose: "Each client's library of posts that got far more views than usual: across its whole niche on TikTok and Instagram, and from the competitors you added. Borrow the idea, never the words.",
    steps: [
      PICK_CLIENT,
      "Across the niche: each client has its own search words (they start from what the client does, like junk removal or house painting). Change them, pick Past month or Past 3 months (TikTok only; Instagram has no date filter) and the platforms, then press Find viral posts (about 1 Scrape Creators credit for each platform).",
      "Find more reads the next page of the same search and adds it to the library. Refresh now runs the search again.",
      "Switch between Viral only and Everything found, pick a platform, and sort by Most viral, Newest found or Most views. Posts aimed at people running this kind of business, and off-topic ones, are hidden; tick the box to see them.",
      "Click a post to see it and ask Why it worked. Pick who it is for under Make this for (this client, your agency or any other client), then Write our version (a draft in that client's Content review) or Save as idea (on that client's Content pipeline).",
      "From competitors: the viral posts of the competitors added on Competitors.",
    ],
    reading: [
      "99x followers means the post got 99 times as many views as the account has followers: a small account breaking out, which is the kind worth copying. A big account's normal reach does not count.",
      "Typical (Instagram) compares a reel with the other reels the same search found, because Instagram does not say how many followers the account has.",
      "Found this week marks posts first found in the last 7 days. Near you means the post names one of the client's towns.",
    ],
    goodToKnow: [
      "Only US accounts and English posts are kept.",
      "Every post found stays in the client's library, and each client's search runs again by itself once a week.",
      "Clients with the same search words share one search, so the Junk King franchises cost one search, not one each. The same search within 3 days answers from the library.",
      "Instagram's search shows its top reels of all time for the words, so some are months old.",
      "Generate with AI on the Content pipeline reads the client's viral posts.",
    ],
  },

  "/content": {
    title: "Content review",
    purpose: "Write, preview and approve client content like ads, Google posts, texts and review replies.",
    steps: [
      PICK_CLIENT,
      "Under New content, pick a Type, give it a title and press Create draft.",
      "Click an item in the list to edit it. The preview on the right shows how it will look.",
      "Press Save changes, then Start review, then Approve.",
      "After you post it on the platform, paste the Published URL and press Mark as posted.",
    ],
    reading: [
      "Status: Draft, In review (amber), Approved and Posted (green).",
      "Character counters (for example 25/30) turn red when text is too long for the platform.",
      "RSA: a Google responsive search ad. GBP post: a Google Business Profile post.",
    ],
    goodToKnow: [
      "Approving does not post anything. Posting is done by hand on the platform.",
      "Approved content can also be queued on the Creative hub's Posting queue tab. Recording it as published there marks it posted here.",
    ],
  },

  "/creative": {
    title: "Creative hub",
    purpose: "The client's images, files and designs, plus tools to make new ones and track what gets posted.",
    steps: [
      PICK_CLIENT,
      "Brand files: the client's asset links and private files. Press Add a link, or open \"Store a file privately in NOLA\".",
      "Google Drive: press Upload to Drive or Link Canva design.",
      "Make an image: describe the image you need and press Generate image. Check it, then save it to Drive.",
      "Check copy: paste text, pick its content type and press Check copy.",
      "Posting queue: press Queue approved content and pick an approved draft, post it on the platform, then press Record as published.",
    ],
    reading: [
      "Google Drive lists only files saved to Google Drive or linked from Canva through NOLA, not everything in Google Drive.",
      "Check copy: errors must be fixed before posting; warnings and suggestions are optional. For a law firm it also flags anything bar rules forbid.",
      "In the posting queue, Pending means it still needs to be posted by hand.",
    ],
    goodToKnow: [
      "Image generation and copy checks use the AI service set up in Settings and may cost money.",
      "Drive uploads must be 4 MB or less. Files stored privately in NOLA can be up to 100 MB.",
    ],
  },

  "/projects": {
    title: "Client projects",
    purpose: "This client's projects, their phases and the tasks inside them.",
    steps: [
      PICK_CLIENT,
      "Press New project to start one for this client. Most types come with a standard set of phases.",
      "List tab: this client's projects. Click one for details, then Edit record to change its name, due date, priority or status.",
      "Tasks tab: this client's tasks in columns. Click a card to open the task; use the arrows button on a card to move it.",
      "Phases tab: pick a project, then press Update phase to change a phase's status or note what it is waiting on.",
    ],
    reading: [
      "Tasks columns: Backlog, To do, In progress, Review and Done. A card shows its project when it belongs to one.",
      "A red date on a card means the task is overdue.",
      "Phase status: Pending, Active, Blocked, Completed or Skipped. Completing or skipping a phase starts the phases that wait on it.",
    ],
  },

  "/onboarding/:token": {
    title: "Client onboarding",
    purpose: "A secure questionnaire for your agency.",
    reading: ["Review shows the answers that will be submitted. Blank fields do not erase existing values."],
    steps: ["Complete Business, Brand, Performance, and Contact & goals.", "Leave unknown answers blank. Keep this page open until you submit.", "Review your answers, then press Submit answers."],
    goodToKnow: ["Your agency reviews the recorded information before launch. Links expire after seven days."],
  },
  "/onboarding": {
    title: "Onboarding",
    purpose: "Collect client information and see what still needs attention.",
    steps: [
      PICK_CLIENT,
      "Press Continue setup, or Edit on a section. Only the answers you change are saved.",
      "Review the answers and press Save answers.",
      "Once the client confirms the booking rate and average job value, press Mark verified on Performance.",
      "Admins can create a private client link for the client to fill in themselves, and switch all links off.",
    ],
    reading: [
      "Recorded means a value exists. Needed means it is missing.",
      "Booking rate and average job value show Unverified until someone confirms them with the client, then Verified. ROAS and the AI analyst only trust verified figures.",
      "Ready for launch review needs at least 70% of answers plus a booking rate and an average job value. It does not launch campaigns.",
    ],
    goodToKnow: [
      "A client link works for 7 days and also opens the client's portal, so send it only to the client's approved contact.",
      "Admins connect reporting and portal tools in Setup → Connections and Setup → Portal, after intake.",
    ],
  },

  "/sops": {
    title: "SOPs",
    purpose: "Step-by-step checklists (standard operating procedures) so the work for this client is done the same way every time.",
    steps: [
      PICK_CLIENT,
      "Find the procedure and press Start (or Continue if one is already open for this client).",
      "Work down the steps in order, ticking each one when it is done. Add a note with evidence or links.",
      "If a step does not apply, an admin can waive it with a reason.",
      "See this client's open and finished checklists under Checklist runs (In progress and Completed tabs).",
    ],
    reading: [
      "Each procedure shows how many steps it has and about how long it takes.",
      "Codes like KB-007 open the Knowledge base document beside the checklist, so you keep your place.",
      "Not written yet: procedures with no steps so far. They can't be started until their steps are added.",
      "Administrator step: only an admin can tick it.",
    ],
  },

  "/portal": {
    title: "Your client portal",
    purpose: "See how your marketing is doing, read your call reports, and choose which alerts you get.",
    steps: [
      "Use the sidebar to open Overview, Call reports, Calls, Your requests and Settings. Audience tools shows up there when your agency turns it on.",
      "Overview shows recorded spend, calls, revenue and ROAS. Pick the period at the top: this week, this month, last month, this quarter or last quarter.",
      "Calls: your Local Services Ads calls. Open a call to hear it and tick Called back once your office has called them.",
      "Call reports: click Upload a call center report to send us your call center file, or press Read report to see a past one. You can type a question under Ask about this report.",
      "Settings → Notifications: choose your email, how often you hear from us, and which alerts you want. Press Save preferences.",
      "Audience tools (when on): upload a list of your customers to get a protected file ready for Google Ads.",
    ],
    reading: [
      "Ad spend: what was spent on your ads in the period you picked.",
      "Call center calls: calls counted in your call center report for the period.",
      "Completed revenue: money from jobs that were finished.",
      "ROAS (return on ad spend): revenue divided by ad spend. 4× means reported revenue is four times recorded spend; this does not establish ad attribution.",
      "A dash (—) means we don't have that number yet. We never guess missing numbers.",
    ],
    goodToKnow: [
      "Customer match scrambles (hashes) every email and phone number before you download it, and it does not upload anything to Google Ads for you.",
      "Questions? Contact your No Limits Marketing account manager.",
    ],
  },

  "/forgot-password": {
    title: "Recover your account", purpose: "Request an email link to reset your NOLA password.",
    steps: ["Enter the email you sign in to NOLA with.", "Press Send reset link.", "Open the email and choose a new password within 30 minutes."],
    reading: ["The confirmation does not reveal whether an account exists."],
    goodToKnow: ["An administrator must configure Email (Resend) in Settings, Integrations first."],
  },
  "/reset-password": {
    title: "Reset your password", purpose: "Choose a new password using your single-use email link.",
    steps: ["Enter a unique password of at least 12 characters.", "Confirm the same password and press Update password.", "Return to sign in with your new password."],
    reading: ["Reset links expire after 30 minutes. An expired or used link requires a new request."],
    goodToKnow: ["A successful reset ends your existing sessions."],
  },
  "/login": {
    title: "Sign in",
    purpose: "Sign in to NOLA with your work email and password.",
    steps: [
      "Type your work email and password.",
      "Click the eye button to show or hide your password.",
      "Press Sign in.",
    ],
    reading: ["If sign in fails, a red message explains why."],
    goodToKnow: ["Can't sign in? Ask your agency admin to check your account."],
  },
};

// The portal link a client is emailed (/portal/<token>) is the same page.
PAGE_HELP["/portal/:token"] = PAGE_HELP["/portal"];
PAGE_HELP["/client-login"] = { ...PAGE_HELP["/login"], title: "Client sign in", purpose: "Sign in to your company’s client workspace.", goodToKnow: ["Your agency creates your client login. Use Forgot password to choose a new password through a secure email link."] };

export const GENERIC_HELP: PageHelp = {
  title: "Getting around NOLA",
  purpose: "NOLA is the agency's workspace for clients, work and results.",
  steps: [
    "Use the sidebar on the left to move between pages.",
    "The Agency tab has the agency's own work. The Clients tab has pages for one client at a time.",
    "Press Search workspace at the top (or Ctrl+K / Cmd+K) to jump to any page or client.",
    "Press the Help button on any page to see what that page does.",
  ],
  reading: ["Most pages explain their numbers in the small grey line under each card or heading."],
};

/** Route patterns, most specific first so "/clients/:id" never shadows a fixed path. */
const PATTERNS = Object.keys(PAGE_HELP).sort((a, b) => b.split("/").length - a.split("/").length || b.length - a.length);

/** The App.tsx route pattern this pathname belongs to, or null. Matches ":param" segments. */
export function matchHelpRoute(pathname: string): string | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  if (PAGE_HELP[path]) return path;
  const parts = path.split("/");
  for (const pattern of PATTERNS) {
    const want = pattern.split("/");
    if (want.length !== parts.length) continue;
    if (want.every((seg, i) => (seg.startsWith(":") ? parts[i].length > 0 : seg === parts[i]))) return pattern;
  }
  return null;
}

/** The help entry for a pathname; the sidebar guide when the route is unknown. */
export function helpFor(pathname: string): PageHelp {
  const key = matchHelpRoute(pathname);
  return key ? PAGE_HELP[key] : GENERIC_HELP;
}
